import { useTranslation } from "react-i18next";
import { useProductContext } from "~/domains/product/ProductContext";
import { CURRENT_LANGUAGE } from "~/utils/constants";
import { userIsCustomer, userIsRegistered } from "~/domains/user/utils/roles";
import type { TFunction } from "i18next";
import type { ProductData } from "~/domains/product/types/ProductData";
import { useCommerceContext, useSessionUser } from "~/app/config/useSession";
import type { CommerceContextData } from "~/app/store/bffApi";

export function useResolveValue(value: string): string {
  const { t } = useTranslation();
  const baseUser = useSessionUser();
  const selectedAccount = useCommerceContext()?.selectedAccount;
  let product = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    product = useProductContext();
  } catch {
    // Product is not available - we are not in PDP
  }

  if (value.startsWith("{") && value.endsWith("}")) {
    let result: string | Record<string, unknown> = createContext(product, t, baseUser, selectedAccount);

    const parts = value.substring(1, value.length - 1).split(".");
    for (const part of parts) {
      if (!(result instanceof Object)) {
        break;
      }
      result = result?.[part] as string | Record<string, unknown>;
    }
    return typeof result === "string" ? result : "";
  }

  return value;
}

function createContext(
  product: ProductData | null,
  t: TFunction,
  baseUser: ReturnType<typeof useSessionUser>,
  selectedAccount: CommerceContextData["selectedAccount"],
) {
  const languageCode = CURRENT_LANGUAGE.substring(0, 2);

  const commonUserData = baseUser
    ? {
        firstName: baseUser?.firstName ?? "",
        lastName: baseUser?.lastName ?? "",
        email: baseUser?.email ?? "",
        salutation: baseUser?.salutationCode ?? "",
      }
    : null;

  const customer = (() => {
    if (userIsCustomer()) {
      return {
        ...commonUserData,
        company: selectedAccount?.name ?? "",
        country: baseUser?.country ?? "",
      };
    }

    if (userIsRegistered()) {
      return {
        ...commonUserData,
        company: baseUser?.company ?? "",
        industrySector: baseUser?.industrySectorName ?? "",
        // phone: baseUser?.phone ?? "", Not in AC
        country: baseUser?.country ?? "",
      };
    }

    return undefined;
  })();

  const context = {
    product: product,
    location: window.location,
    category: {
      name: window.SCHA.meta?.categoryNameEn ?? "",
    },
    currentLanguage: {
      code: languageCode,
      name: t(`languageName.${languageCode}`),
    },
    customer,
  };
  return context;
}
